import { action, atom, onConnect } from '@reatom/framework'

import { pipe } from '@blade/std-lib'
import { Result } from '@blade/std-lib'
import { Comment } from '@root/entities/comment'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib/reatom'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'

import type { CommentApi } from '@root/shared/api'
import type { Period } from '@root/shared/period'

export const commentsHistoryTypeAtom = atom<Comment.CommentType>(
  'comments' satisfies Comment.CommentType,
  'commentsHistoryTypeAtom',
)
export const commentsHistoryPeriodAtom = atom<Period.Period>(
  'ALL' satisfies Period.Period,
  'commentsHistoryPeriodAtom',
)

export const fetchCommentsHistoryRequestAction = action(
  async (ctx, req: CommentApi.GetCommentsHistoryReqDto) => {
    const { tradeApiClient, environment } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.comment.activityUserCommentsList, [
        {
          delugeId: req.delugeId,
          period: req.period,
          type: req.type,
          network: req.network,
          offset: req.offset,
          limit: req.limit,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map((it) =>
        Comment.commentsHistoryFromDto(it, {
          avatarHost: environment.assets.avatarsHost,
          tokenLogosHost: environment.assets.tokenLogosHost,
        }),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchCommentsHistoryRequestAction',
)

export const reatomCommentsHistory = ({
  type,
  period,
  network,
  delugeId,
}: CommentApi.GetCommentsHistoryReqDto) => {
  const cursorAtom = atom<number>(0, 'cursorAtom')
  const hasMoreItemsAtom = atom<boolean>(true, 'hasMoreItemsAtom')
  const historyAtom = atom<Array<Comment.CommentsHistory>>(
    [],
    // eslint-disable-next-line @reatom/unit-naming-rule
    `commentsHistoryAtom_${type}_${period}_${network}`,
  )

  const {
    executeAction: loadCommentsAction,
    requestStateAtom: loadCommentsRequestState,
  } = reatomRequest({
    execAction: fetchCommentsHistoryRequestAction,
    name: `loadCommentsHistoryRequest_${type}_${period}_${network}`,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      const cursor = ctx.get(cursorAtom)
      const historyExisting = ctx.get(historyAtom)
      const historyNew = [...historyExisting, ...result.success]

      historyAtom(ctx, historyNew)
      hasMoreItemsAtom(
        ctx,
        result.success.length === Comment.COMMENT_HISTORY_PAGINATION,
      )
      cursorAtom(ctx, cursor + 1)
    },
  })

  const reloadCommentsAction = action(async (ctx) => {
    loadCommentsAction(ctx, {
      type,
      period,
      network,
      delugeId,
      offset: 0,
      limit: Comment.COMMENT_HISTORY_PAGINATION,
    })
  }, 'reloadCommentsAction')

  const loadCommentsMoreAction = action(async (ctx) => {
    const cursor = ctx.get(cursorAtom)
    loadCommentsAction(ctx, {
      type,
      period,
      network,
      delugeId,
      offset: cursor,
      limit: Comment.COMMENT_HISTORY_PAGINATION,
    })
  }, 'loadCommentsMoreAction')

  onConnect(loadCommentsRequestState, loadCommentsMoreAction)

  return {
    historyAtom,
    hasMoreItemsAtom,
    loadCommentsAction,
    loadCommentsMoreAction,
    loadCommentsRequestState,
    reloadCommentsAction,
  }
}

export type CommentsHistoryModel = ReturnType<typeof reatomCommentsHistory>
